/**
 * @file chart-default-config.util.ts
 * @description Computes default data query from customChartConfig without iframe
 * This enables early data fetching while iframe initializes in parallel
 *
 * Performance Impact: ~900ms savings by allowing data fetch to start 1+ second earlier
 */

import {
    getChartType,
    getColumns,
} from '@thoughtspot/chart-lib/lib/core/chart-model-utils';
import {
    getDefaultBatchSize,
    isTsHostedChart,
    muzePhase1Charts,
} from '@thoughtspot/chart-lib/lib/core/chart-type-specification-service';
import { generateDefaultCustomChartConfigForTSHostedCharts } from '@thoughtspot/chart-lib/lib/core/custom-chart-config-utils';
import { envFlags } from '@thoughtspot/env-flags';
import {
    FEATURE_FLAG_NAMES,
    isFeatureEnabled,
} from '@thoughtspot/feature-service';
import { convertCustomChartConfigToDataQueryInput } from '@thoughtspot/ts-chart-app';
import {
    AnswerColumn,
    ChartType,
    ChartViz,
    CustomChartAxisConfig,
    CustomChartConfig,
    DataQueryInput,
    SimpleAxis,
} from '/@services/generated/graphql-types';

/**
 * Filters a simple axis (FlatAxis or MergedAxis) to only include valid columns
 * @param axis - The simple axis configuration (FlatAxis or MergedAxis)
 * @param availableColumnIds - Set of valid column IDs
 * @returns Filtered axis or null if no valid columns remain
 */
function filterSimpleAxis(
    axis: SimpleAxis,
    availableColumnIds: Set<string>,
): SimpleAxis | null {
    // Handle FlatAxis
    // eslint-disable-next-line no-underscore-dangle
    if (axis.__typename === 'FlatAxis') {
        return availableColumnIds.has(axis.column) ? axis : null;
    }

    // Handle MergedAxis
    // eslint-disable-next-line no-underscore-dangle
    if (axis.__typename === 'MergedAxis') {
        const filteredColumns = axis.columns.filter((colId: string) =>
            availableColumnIds.has(colId),
        );
        if (filteredColumns.length === 0) return null;
        return { ...axis, columns: filteredColumns };
    }

    return null;
}

/**
 * Filters a custom chart axis to only include columns that exist in availableColumnIds
 * @param axis - The axis configuration (FlatAxis, MergedAxis, or DualAxis)
 * @param availableColumnIds - Set of valid column IDs
 * @returns Filtered axis or null if no valid columns remain
 */
function filterAxis(
    axis: CustomChartAxisConfig,
    availableColumnIds: Set<string>,
): CustomChartAxisConfig | null {
    if (!axis) return null;

    // Handle DualAxis
    // eslint-disable-next-line no-underscore-dangle
    if (axis.__typename === 'DualAxis') {
        const filteredPrimary = filterSimpleAxis(
            axis.primary,
            availableColumnIds,
        );
        const filteredSecondary = filterSimpleAxis(
            axis.secondary,
            availableColumnIds,
        );

        // Only include if at least one side has columns
        if (!filteredPrimary && !filteredSecondary) return null;

        return {
            ...axis,
            primary: filteredPrimary || axis.primary,
            secondary: filteredSecondary || axis.secondary,
        };
    }

    // Handle FlatAxis and MergedAxis using filterSimpleAxis
    return filterSimpleAxis(axis, availableColumnIds);
}

/**
 * Filters customChartConfig to only include columns that exist in the available columns list
 * This handles both axis-driven and column-driven configurations
 * @param customChartConfigs - Array of custom chart configurations
 * @param availableColumns - Array of available columns
 * @returns Filtered customChartConfig array
 */
function filterCustomChartConfigColumns(
    customChartConfigs: CustomChartConfig[],
    availableColumns: AnswerColumn[],
): CustomChartConfig[] {
    if (!customChartConfigs || customChartConfigs.length === 0) {
        return customChartConfigs;
    }

    // Create a set of available column IDs for fast lookup
    const availableColumnIds = new Set(availableColumns.map(col => col.id));

    return customChartConfigs.map(config => {
        // Clone the config to avoid mutations
        const filteredConfig = { ...config };

        if (!filteredConfig.dimensions) {
            return filteredConfig;
        }

        // Filter each dimension
        filteredConfig.dimensions = filteredConfig.dimensions
            .map(dimension => {
                // Handle axis-driven configuration
                // eslint-disable-next-line no-underscore-dangle
                if (dimension.__typename === 'AxisDrivenChartConfigDimension') {
                    const filteredAxes = (dimension.axes || [])
                        .map(axis => filterAxis(axis, availableColumnIds))
                        .filter(axis => axis !== null);

                    if (filteredAxes.length === 0) return null;

                    return { ...dimension, axes: filteredAxes };
                }

                // Handle column-driven configuration
                if (dimension.columns) {
                    const filteredColumns = dimension.columns.filter(colId =>
                        availableColumnIds.has(colId),
                    );

                    if (filteredColumns.length === 0) return null;

                    return { ...dimension, columns: filteredColumns };
                }

                return dimension;
            })
            .filter(dimension => dimension !== null);

        return filteredConfig;
    });
}

/**
 * Build initial data query for TS-hosted charts
 * This uses the proper dimension mapping that matches what the chart SDK does
 *
 * @param chartViz - Chart visualization metadata from GetChartWithData
 * @returns DataQueryInput to fetch data, or null if unable to compute
 */
export function buildInitialDataQueryForTSHostedCharts(
    chartViz: ChartViz,
): DataQueryInput | null {
    if (!chartViz) {
        return null;
    }

    const chartType = getChartType(chartViz);

    // Only compute for custom and TS-hosted charts
    if (!isTsHostedChart(chartType)) {
        return null;
    }

    const columns = getColumns(chartViz);

    if (columns.length === 0) {
        return null;
    }
    const maxDataPoints =
        chartViz.customVisualProps?.displayVisualProps?.display?.[
            'max-data-points'
        ];
    const defaultBatchSize =
        maxDataPoints === ''
            ? getDefaultBatchSize(chartType)
            : maxDataPoints ?? getDefaultBatchSize(chartType);

    const queryParams = {
        offset: 0,
        size: defaultBatchSize,
    };
    // For Muze phase-1 cartesian charts the BE owns default config generation.
    // Use the existing config if present; skip FE generation so we don't
    // override the BE-computed config with a stale FE-generated one.
    const isMuzeChart = muzePhase1Charts.includes(chartType);
    let customChartConfig =
        chartViz.config.customChartConfig ||
        (isMuzeChart
            ? null
            : generateDefaultCustomChartConfigForTSHostedCharts(
                  chartViz,
                  chartType,
              ));

    // Filter out columns from customChartConfig that are not in the available columns list
    // This prevents issues when columns have been removed from the answer but still exist in the config
    if (customChartConfig) {
        customChartConfig = filterCustomChartConfigColumns(
            customChartConfig,
            columns,
        );
    }

    const isClientSideMnMvCalculationEnabled = isFeatureEnabled(
        FEATURE_FLAG_NAMES.ENABLE_CLIENT_SIDE_MN_MV_CALCULATION,
        {
            urlParamValue: envFlags.getValue(
                FEATURE_FLAG_NAMES.ENABLE_CLIENT_SIDE_MN_MV_CALCULATION,
            ),
        },
    );

    const shouldIncludeMnMvFields =
        !isClientSideMnMvCalculationEnabled &&
        chartType !== ChartType.MuzeStudio;

    const dataQueryInput: DataQueryInput = convertCustomChartConfigToDataQueryInput(
        customChartConfig,
        columns,
        queryParams,
        shouldIncludeMnMvFields,
    );

    return dataQueryInput;
}

/**
 * Check if early data fetch is enabled and beneficial for this chart
 */
export function shouldPrefetchData(chartViz: ChartViz | null): boolean {
    if (!chartViz) {
        return false;
    }

    const chartType = getChartType(chartViz);

    // Only prefetch for custom/TS-hosted charts
    if (!isTsHostedChart(chartType) || chartType === ChartType.MuzeStudio) {
        return false;
    }

    // Must have columns - this is all we need now since we use ALL columns
    const columns = chartViz.columns || [];

    if (columns.length === 0) {
        return false;
    }

    return true;
}
